import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, Search, RefreshCw, X, UserPlus, Pencil, Trash2, AlertTriangle, Trophy, Building2
} from "lucide-react";
import {
  listReferrers, createReferrer, updateReferrer, deleteReferrer,
  listReferrals, getReferral, updateReferral, assignReferral, fetchReferralLeaderboard,
  fetchTenants
} from "../services/api";
import type {
  ReferrerPartner, ReferralSummary, ReferralStatus, ReferrerStatus, ReferrerLeaderboardEntry, ReferralUpdate
} from "../types/referrals";
import type { Tenant } from "../types";
import { useToast } from "../components/ToastProvider";
import { usePageTitle } from "../hooks/usePageTitle";

/* ---------------------------------------------------------------------------
 * Display metadata + money helpers
 * ------------------------------------------------------------------------- */

const COLUMN_ORDER: ReferralStatus[] = ["new", "contacted", "demo", "won", "churned", "lost"];

const STATUS_LABELS: Record<ReferralStatus, string> = {
  new: "New",
  contacted: "Contacted",
  demo: "Demo",
  won: "Won",
  lost: "Lost",
  conflict: "Conflict",
  churned: "Churned"
};

function statusPillClass(s: ReferralStatus): string {
  switch (s) {
    case "won": return "success";
    case "contacted":
    case "demo": return "info";
    case "lost": return "danger";
    case "conflict": return "warn";
    case "churned": return "danger";
    default: return "";
  }
}

function formatRand(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return "R" + (cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

function durationLabel(months: number | null): string {
  if (months == null) return "Forever";
  if (months % 12 === 0) return `${months / 12} yr`;
  return `${months} mo`;
}

/* ---------------------------------------------------------------------------
 * Page shell + tabs
 * ------------------------------------------------------------------------- */

type Tab = "board" | "referrers" | "leaderboard";

export function ReferralsPage() {
  usePageTitle("Referrals");
  const [tab, setTab] = useState<Tab>("board");

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Growth</span>
          <h1>Referrals</h1>
          <p className="muted">External partners refer companies to Orbit and earn monthly commission once they convert.</p>
        </div>
      </header>

      <div className="row" style={{ gap: 8, marginBottom: 4 }}>
        <button className={`tab ${tab === "board" ? "active" : ""}`} onClick={() => setTab("board")}
          style={{ background: "transparent", border: "1px solid var(--border)" }}>Pipeline board</button>
        <button className={`tab ${tab === "referrers" ? "active" : ""}`} onClick={() => setTab("referrers")}
          style={{ background: "transparent", border: "1px solid var(--border)" }}>Referrers</button>
        <button className={`tab ${tab === "leaderboard" ? "active" : ""}`} onClick={() => setTab("leaderboard")}
          style={{ background: "transparent", border: "1px solid var(--border)" }}>Leaderboard</button>
      </div>

      {tab === "board" && <BoardTab />}
      {tab === "referrers" && <ReferrersTab />}
      {tab === "leaderboard" && <LeaderboardTab />}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Pipeline board
 * ------------------------------------------------------------------------- */

function BoardTab() {
  const toast = useToast();
  const [items, setItems] = useState<ReferralSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [assignFor, setAssignFor] = useState<ReferralSummary | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listReferrals();
      setItems(res.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load referrals.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((r) =>
      r.prospectCompany.toLowerCase().includes(s) ||
      r.ref.toLowerCase().includes(s) ||
      r.referrerName.toLowerCase().includes(s) ||
      (r.prospectEmail ?? "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const conflicts = useMemo(() => visible.filter((r) => r.status === "conflict"), [visible]);

  const byStatus = useMemo(() => {
    const groups: Record<ReferralStatus, ReferralSummary[]> = {
      new: [], contacted: [], demo: [], won: [], lost: [], conflict: [], churned: []
    };
    for (const r of visible) groups[r.status].push(r);
    return groups;
  }, [visible]);

  const applyRow = useCallback((updated: ReferralSummary) => {
    setItems((prev) => {
      const next = prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r));
      return next.some((r) => r.id === updated.id) ? next : [updated, ...prev];
    });
  }, []);

  const moveTo = useCallback(async (id: string, toStatus: ReferralStatus) => {
    const current = items.find((r) => r.id === id);
    if (!current || current.status === toStatus) return;
    // Winning a deal requires assigning the converted company -> modal.
    if (toStatus === "won") { setAssignFor(current); return; }
    const prev = current.status;
    setItems((p) => p.map((r) => (r.id === id ? { ...r, status: toStatus } : r)));
    try {
      const res = await updateReferral(id, { status: toStatus });
      applyRow(res.referral);
      toast.push("success", `Moved to ${STATUS_LABELS[toStatus]}.`);
    } catch (err) {
      setItems((p) => p.map((r) => (r.id === id ? { ...r, status: prev } : r)));
      toast.push("error", err instanceof Error ? err.message : "Could not move the referral.");
    }
  }, [items, applyRow, toast]);

  return (
    <>
      <div className="card" style={{ padding: 16 }}>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="search-bar" style={{ flex: "1 1 280px", maxWidth: "none" }}>
            <Search size={14} />
            <input type="search" placeholder="Search by company, ref, referrer, email…"
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <button className="btn btn-ghost" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : undefined} /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="card" style={{ borderColor: "rgba(255,92,122,0.3)" }}>
          <p style={{ color: "var(--danger)", margin: 0 }}>{error}</p>
        </div>
      ) : null}

      {conflicts.length > 0 ? (
        <div className="card" style={{ padding: 14, borderColor: "var(--warn)", background: "var(--warn-soft)" }}>
          <div className="row" style={{ gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={16} style={{ color: "var(--warn)" }} />
            <strong>{conflicts.length} possible duplicate{conflicts.length > 1 ? "s" : ""} need review</strong>
          </div>
          <div className="col" style={{ gap: 8 }}>
            {conflicts.map((r) => (
              <div key={r.id} className="row between wrap" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <span className="mono">{r.ref}</span> · <strong>{r.prospectCompany}</strong>
                  <span className="muted"> by {r.referrerName}</span>
                  {r.conflictReason ? <div className="muted" style={{ fontSize: 12 }}>{r.conflictReason}</div> : null}
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(r.id)}>Review</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 6 }}>
        {COLUMN_ORDER.map((status) => (
          <BoardColumn
            key={status}
            status={status}
            referrals={byStatus[status]}
            loading={loading}
            draggingId={draggingId}
            onOpen={setOpenId}
            onDragStart={setDraggingId}
            onDragEnd={() => setDraggingId(null)}
            onDrop={moveTo}
          />
        ))}
      </div>

      {assignFor ? (
        <AssignModal
          referral={assignFor}
          onClose={() => setAssignFor(null)}
          onAssigned={(r) => { applyRow(r); setAssignFor(null); toast.push("success", `${r.prospectCompany} assigned to ${r.referrerName}.`); }}
        />
      ) : null}

      {openId ? (
        <ReferralDetailModal
          id={openId}
          rows={items}
          onClose={() => setOpenId(null)}
          onChanged={applyRow}
          onAssign={(r) => { setOpenId(null); setAssignFor(r); }}
        />
      ) : null}
    </>
  );
}

function BoardColumn({
  status, referrals, loading, draggingId, onOpen, onDragStart, onDragEnd, onDrop
}: {
  status: ReferralStatus;
  referrals: ReferralSummary[];
  loading: boolean;
  draggingId: string | null;
  onOpen: (id: string) => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDrop: (id: string, status: ReferralStatus) => void;
}) {
  const [over, setOver] = useState(false);
  const canDrop = draggingId != null;
  const total = referrals.reduce((sum, r) => sum + (r.monthlyEarningsCents || 0), 0);
  return (
    <section
      className={"support-col" + (over && canDrop ? " is-drop-target" : "")}
      style={{ minWidth: 264, flex: "0 0 264px" }}
      onDragOver={(e) => { if (canDrop) { e.preventDefault(); setOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (draggingId) onDrop(draggingId, status); }}
    >
      <div className="support-col-head">
        <h3 style={{ margin: 0 }}>{STATUS_LABELS[status]}</h3>
        <span className="support-col-count">{referrals.length}</span>
      </div>
      {status === "won" && total > 0 ? (
        <div className="muted" style={{ fontSize: 11.5, padding: "0 4px 6px" }}>{formatRand(total)}/mo earning</div>
      ) : null}
      <div className="support-col-body">
        {loading && referrals.length === 0 ? (
          <p className="muted" style={{ padding: "8px 4px", fontSize: 12.5 }}>Loading…</p>
        ) : referrals.length === 0 ? (
          <p className="muted" style={{ padding: "8px 4px", fontSize: 12.5 }}>None.</p>
        ) : (
          referrals.map((r) => (
            <ReferralCard key={r.id} referral={r} dragging={draggingId === r.id}
              onOpen={onOpen} onDragStart={onDragStart} onDragEnd={onDragEnd} />
          ))
        )}
      </div>
    </section>
  );
}

function ReferralCard({
  referral, dragging, onOpen, onDragStart, onDragEnd
}: {
  referral: ReferralSummary;
  dragging: boolean;
  onOpen: (id: string) => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
}) {
  return (
    <button type="button"
      className={"card card-hover support-card" + (dragging ? " is-dragging" : "")}
      draggable
      onClick={() => onOpen(referral.id)}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", referral.id);
        onDragStart(referral.id);
      }}
      onDragEnd={onDragEnd}
    >
      <div className="row between" style={{ gap: 8 }}>
        <span className="mono support-ref">{referral.ref}</span>
        {referral.status === "won" && referral.monthlyValueCents ? (
          <span className="pill success">{formatRand(referral.monthlyValueCents)}/mo</span>
        ) : null}
      </div>
      <div className="support-card-subject">{referral.prospectCompany}</div>
      <div className="row between" style={{ gap: 8, marginTop: 4 }}>
        <span className="support-card-company"><UserPlus size={12} /> {referral.referrerName}</span>
        <span className="faint" style={{ fontSize: 11 }}>{shortDate(referral.createdAt)}</span>
      </div>
    </button>
  );
}

/* ---------------------------------------------------------------------------
 * Assign modal — convert a referral to "won" by attaching the tenant.
 * ------------------------------------------------------------------------- */

function AssignModal({
  referral, onClose, onAssigned
}: {
  referral: ReferralSummary;
  onClose: () => void;
  onAssigned: (r: ReferralSummary) => void;
}) {
  const toast = useToast();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantQuery, setTenantQuery] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [ratePct, setRatePct] = useState(referral.commissionRatePct != null ? String(referral.commissionRatePct) : "");
  const [forever, setForever] = useState(false);
  const [durationMonths, setDurationMonths] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    fetchTenants().then((t) => { if (active) setTenants(t); }).catch(() => undefined);
    return () => { active = false; };
  }, []);

  const matches = useMemo(() => {
    const s = tenantQuery.trim().toLowerCase();
    const base = s ? tenants.filter((t) => t.name.toLowerCase().includes(s) || t.slug.toLowerCase().includes(s)) : tenants;
    return base.slice(0, 8);
  }, [tenants, tenantQuery]);

  function pickTenant(t: Tenant) {
    setTenantId(t.id);
    setTenantQuery(t.name);
    // Leave the monthly value blank — when omitted the backend defaults it from
    // the tenant's actual subscription amount, so earnings still compute.
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!tenantId) { toast.push("error", "Pick the converted company (tenant) first."); return; }
    setBusy(true);
    try {
      const input: { businessId: string; monthlyValueCents?: number; commissionRatePct?: number; commissionDurationMonths?: number | null } = {
        businessId: tenantId
      };
      if (ratePct.trim()) input.commissionRatePct = parseFloat(ratePct);
      if (forever) input.commissionDurationMonths = null;
      else if (durationMonths.trim()) input.commissionDurationMonths = parseInt(durationMonths, 10);
      const res = await assignReferral(referral.id, input);
      onAssigned(res.referral);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not assign the company.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Assign won company</h2>
        <p>
          Mark <strong>{referral.ref}</strong> ({referral.prospectCompany}) as won and start{" "}
          <strong>{referral.referrerName}</strong>'s commission. Pick the Orbit company this became.
        </p>
        <form className="form" onSubmit={submit}>
          <label>
            Converted company (tenant)
            <input value={tenantQuery} placeholder="Search companies…"
              onChange={(e) => { setTenantQuery(e.target.value); setTenantId(""); }} />
          </label>
          {!tenantId && tenantQuery.trim() ? (
            <div className="card" style={{ padding: 6, maxHeight: 180, overflowY: "auto" }}>
              {matches.length === 0 ? (
                <p className="muted" style={{ margin: 6, fontSize: 12.5 }}>No companies match.</p>
              ) : matches.map((t) => (
                <button key={t.id} type="button" className="btn btn-ghost btn-sm"
                  style={{ display: "flex", width: "100%", justifyContent: "flex-start", gap: 8 }}
                  onClick={() => pickTenant(t)}>
                  <Building2 size={13} /> {t.name} <span className="muted">· {t.slug}</span>
                </button>
              ))}
            </div>
          ) : null}
          <div className="form-grid">
            <label>
              Commission %
              <input inputMode="decimal" value={ratePct} placeholder={`default ${referral.commissionRatePct ?? 10}%`}
                onChange={(e) => setRatePct(e.target.value)} />
            </label>
            <label>
              Duration (months)
              <input inputMode="numeric" value={durationMonths} disabled={forever} placeholder="referrer default"
                onChange={(e) => setDurationMonths(e.target.value)} />
            </label>
            <label className="row" style={{ gap: 8, alignItems: "center", marginTop: 22 }}>
              <input type="checkbox" checked={forever} onChange={(e) => setForever(e.target.checked)} style={{ width: "auto" }} />
              Pays forever
            </label>
          </div>
          <p className="muted" style={{ fontSize: 12 }}>
            The company's package price is pulled automatically from their plan. Leave commission % or duration blank to use the referrer's saved terms.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !tenantId}>
              {busy ? "Assigning…" : "Mark won & assign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Referral detail modal — edit status / value, resolve conflicts.
 * ------------------------------------------------------------------------- */

function ReferralDetailModal({
  id, rows, onClose, onChanged, onAssign
}: {
  id: string;
  rows: ReferralSummary[];
  onClose: () => void;
  onChanged: (r: ReferralSummary) => void;
  onAssign: (r: ReferralSummary) => void;
}) {
  const toast = useToast();
  const [enriched, setEnriched] = useState<ReferralSummary | null>(null);
  const [updates, setUpdates] = useState<ReferralUpdate[]>([]);
  const referral = enriched ?? rows.find((r) => r.id === id) ?? null;
  const [ratePct, setRatePct] = useState(referral?.commissionRatePct != null ? String(referral.commissionRatePct) : "");
  const [busy, setBusy] = useState(false);

  // Fetch the cost/profit-enriched detail + the referrer's activity log.
  useEffect(() => {
    let on = true;
    getReferral(id)
      .then((r) => { if (on) { setEnriched(r.referral); setUpdates(r.updates || []); } })
      .catch(() => undefined);
    return () => { on = false; };
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!referral) return null;

  async function mutate(patchInput: Parameters<typeof updateReferral>[1], successMsg: string) {
    setBusy(true);
    try {
      const res = await updateReferral(referral!.id, patchInput);
      onChanged(res.referral);
      toast.push("success", successMsg);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not update the referral.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="row between" style={{ gap: 10 }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="mono support-ref" style={{ fontSize: 14 }}>{referral.ref}</span>
            <span className={`pill ${statusPillClass(referral.status)}`}>{STATUS_LABELS[referral.status]}</span>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <h2 style={{ margin: "10px 0 2px" }}>{referral.prospectCompany}</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Referred by <strong>{referral.referrerName}</strong> · {shortDate(referral.createdAt)}
        </p>

        <div className="col" style={{ gap: 4, fontSize: 13, marginBottom: 12 }}>
          {referral.prospectContactName ? <div><span className="muted">Contact:</span> {referral.prospectContactName}</div> : null}
          {referral.prospectEmail ? <div><span className="muted">Email:</span> {referral.prospectEmail}</div> : null}
          {referral.prospectPhone ? <div><span className="muted">Phone:</span> {referral.prospectPhone}</div> : null}
          {referral.businessName ? <div><span className="muted">Orbit company:</span> {referral.businessName}</div> : null}
          {referral.status === "won" ? (
            <div className="col" style={{ gap: 4, marginTop: 4 }}>
              <div>
                <span className="muted">Terms:</span> {referral.commissionRatePct ?? "—"}% of profit ·{" "}
                {shortDate(referral.commissionStart)} → {referral.commissionEnd ? shortDate(referral.commissionEnd) : "forever"}
              </div>
              <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
                <span><span className="muted">Package</span> {formatRand(referral.monthlyValueCents)}</span>
                <span><span className="muted">Costs</span> {referral.monthlyCostCents === undefined ? "…" : formatRand(referral.monthlyCostCents)}</span>
                <span><span className="muted">Profit</span> {referral.monthlyProfitCents === undefined ? "…" : formatRand(referral.monthlyProfitCents)}</span>
                <span><span className="muted">Commission</span> <strong>{formatRand(referral.monthlyEarningsCents)}/mo</strong></span>
              </div>
            </div>
          ) : null}
        </div>

        {referral.status === "conflict" ? (
          <div className="card" style={{ padding: 12, borderColor: "var(--warn)", background: "var(--warn-soft)", marginBottom: 12 }}>
            <div className="row" style={{ gap: 8, marginBottom: 6 }}>
              <AlertTriangle size={15} style={{ color: "var(--warn)" }} />
              <strong>Possible duplicate</strong>
            </div>
            {referral.conflictReason ? <p className="muted" style={{ margin: "0 0 10px", fontSize: 12.5 }}>{referral.conflictReason}</p> : null}
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-ghost btn-sm" disabled={busy}
                onClick={() => void mutate({ status: "new", clearConflict: true }, "Conflict cleared — back to New.")}>
                Keep (it's a new company)
              </button>
              <button className="btn btn-ghost btn-sm" disabled={busy}
                onClick={() => void mutate({ status: "lost", clearConflict: true }, "Marked as a duplicate (Lost).")}>
                Reject as duplicate
              </button>
            </div>
          </div>
        ) : null}

        {referral.status === "won" ? (
          <div className="form" style={{ marginBottom: 12 }}>
            <label style={{ maxWidth: 200 }}>
              Commission %
              <input inputMode="decimal" value={ratePct} onChange={(e) => setRatePct(e.target.value)} />
            </label>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void mutate({
              commissionRatePct: ratePct.trim() ? parseFloat(ratePct) : null
            }, "Commission rate updated.")}>
              Save commission rate
            </button>
            <p className="muted" style={{ fontSize: 11.5 }}>
              Package price tracks the company's plan automatically. Commission is their % of profit (package − running costs).
            </p>
          </div>
        ) : null}

        {updates.length > 0 ? (
          <div style={{ marginBottom: 12 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Activity (logged by the referrer)</div>
            <div className="col" style={{ gap: 8, maxHeight: 220, overflowY: "auto" }}>
              {[...updates].reverse().map((u) => (
                <div key={u.id} style={{ borderLeft: "2px solid var(--border)", paddingLeft: 10 }}>
                  <div className="faint" style={{ fontSize: 11 }}>{u.kind} · {u.authorName} · {shortDate(u.createdAt)}</div>
                  <div style={{ fontSize: 13 }}>{u.body}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="modal-actions">
          {referral.status === "won" ? (
            <button className="btn btn-ghost" disabled={busy}
              onClick={() => void mutate({ status: "churned" }, "Marked churned — commission stops.")}>
              Mark churned
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => onAssign(referral)}>
              Mark won & assign company
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Referrers tab — CRUD + commission terms
 * ------------------------------------------------------------------------- */

function ReferrersTab() {
  const toast = useToast();
  const [items, setItems] = useState<ReferrerPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<ReferrerPartner | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ReferrerPartner | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listReferrers();
      setItems(res.items ?? []);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not load referrers.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const s = query.trim().toLowerCase();
    if (!s) return items;
    return items.filter((r) =>
      r.fullName.toLowerCase().includes(s) ||
      r.email.toLowerCase().includes(s) ||
      (r.company ?? "").toLowerCase().includes(s)
    );
  }, [items, query]);

  return (
    <>
      <div className="card" style={{ padding: 16 }}>
        <div className="row wrap between" style={{ gap: 12 }}>
          <div className="search-bar" style={{ flex: "1 1 280px" }}>
            <Search size={14} />
            <input type="search" placeholder="Search referrers by name, email, company…"
              value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={14} /> New referrer
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Referrer</th>
              <th>Status</th>
              <th>Commission</th>
              <th>Window</th>
              <th>Referrals</th>
              <th>Earning</th>
              <th>Monthly</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}><div className="empty-state"><p className="muted">Loading referrers…</p></div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8}><div className="empty-state"><h3>No referrers yet</h3><p>Add one to get them into the program.</p></div></td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="row-link-name">{r.fullName}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {r.email}{r.company ? ` • ${r.company}` : ""}
                    </div>
                  </td>
                  <td><span className={`pill ${r.status === "active" ? "success" : r.status === "suspended" ? "danger" : "warn"}`}>
                    {r.status === "active" ? "Active" : r.status === "suspended" ? "Suspended" : "Invited"}
                  </span></td>
                  <td className="tnum">{r.commissionRatePct}%</td>
                  <td className="muted">{durationLabel(r.commissionDurationMonths)}</td>
                  <td className="tnum">{r.referralCount ?? 0}</td>
                  <td className="tnum">{r.activeCompanyCount ?? 0}</td>
                  <td className="tnum">{formatRand(r.monthlyEarningsCents ?? 0)}</td>
                  <td className="right">
                    <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <button className="icon-btn" title="Edit" onClick={() => setEditing(r)}><Pencil size={15} /></button>
                      <button className="icon-btn" title="Delete" onClick={() => setDeleting(r)}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(creating || editing) ? (
        <ReferrerFormModal
          existing={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={(r, isNew) => {
            setItems((prev) => isNew ? [r, ...prev] : prev.map((x) => (x.id === r.id ? r : x)));
            setCreating(false); setEditing(null);
            toast.push("success", isNew ? `Added ${r.fullName}.` : `Updated ${r.fullName}.`);
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmModal
          title="Delete referrer?"
          body={`This permanently removes ${deleting.fullName} and all their referrals. This cannot be undone.`}
          confirmLabel="Delete referrer"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              await deleteReferrer(deleting.id);
              setItems((prev) => prev.filter((x) => x.id !== deleting.id));
              toast.push("success", `Deleted ${deleting.fullName}.`);
            } catch (err) {
              toast.push("error", err instanceof Error ? err.message : "Could not delete the referrer.");
            } finally {
              setDeleting(null);
            }
          }}
        />
      ) : null}
    </>
  );
}

function ReferrerFormModal({
  existing, onClose, onSaved
}: {
  existing: ReferrerPartner | null;
  onClose: () => void;
  onSaved: (r: ReferrerPartner, isNew: boolean) => void;
}) {
  const isNew = !existing;
  const [fullName, setFullName] = useState(existing?.fullName ?? "");
  const [email, setEmail] = useState(existing?.email ?? "");
  const [phone, setPhone] = useState(existing?.phone ?? "");
  const [company, setCompany] = useState(existing?.company ?? "");
  const [ratePct, setRatePct] = useState(String(existing?.commissionRatePct ?? 10));
  const [forever, setForever] = useState(existing ? existing.commissionDurationMonths == null : false);
  const [durationMonths, setDurationMonths] = useState(
    existing?.commissionDurationMonths != null ? String(existing.commissionDurationMonths) : "24"
  );
  const [status, setStatus] = useState<ReferrerStatus>(existing?.status ?? "invited");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const duration = forever ? null : (durationMonths.trim() ? parseInt(durationMonths, 10) : 24);
      const rate = ratePct.trim() ? parseFloat(ratePct) : 10;
      if (isNew) {
        const res = await createReferrer({
          email: email.trim(), fullName: fullName.trim(),
          phone: phone.trim() || null, company: company.trim() || null,
          commissionRatePct: rate, commissionDurationMonths: duration, notes: notes.trim() || null
        });
        onSaved(res.referrer, true);
      } else {
        const res = await updateReferrer(existing!.id, {
          fullName: fullName.trim(), phone: phone.trim() || null, company: company.trim() || null,
          status, commissionRatePct: rate, commissionDurationMonths: duration, notes: notes.trim() || null
        });
        onSaved(res.referrer, false);
      }
    } catch {
      // surfaced by the table's toast on reload; keep modal open
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isNew ? "New referrer" : "Edit referrer"}</h2>
        <p>
          {isNew
            ? "They'll sign into the partner portal with this email and set a password on first login."
            : "Commission terms are private — they're never shown to the referrer or on the leaderboard."}
        </p>
        {!isNew && existing ? (
          <div className="card" style={{ padding: 10, marginBottom: 12 }}>
            <div className="row between" style={{ gap: 8 }}>
              <span className="muted" style={{ fontSize: 12 }}>Referral link (signups through it auto-assign)</span>
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={() => navigator.clipboard?.writeText(existing.referralLink || existing.referralCode)}>Copy</button>
            </div>
            <div className="mono" style={{ wordBreak: "break-all", fontSize: 12 }}>{existing.referralLink || existing.referralCode}</div>
          </div>
        ) : null}
        <form className="form" onSubmit={submit}>
          <div className="form-grid">
            <label>
              Full name
              <input required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Thabo Nkosi" />
            </label>
            <label>
              Email
              <input type="email" required value={email} disabled={!isNew}
                onChange={(e) => setEmail(e.target.value)} placeholder="thabo@example.com" />
            </label>
            <label>
              Phone
              <input value={phone ?? ""} onChange={(e) => setPhone(e.target.value)} placeholder="+27…" />
            </label>
            <label>
              Their company (optional)
              <input value={company ?? ""} onChange={(e) => setCompany(e.target.value)} />
            </label>
            <label>
              Commission %
              <input inputMode="decimal" value={ratePct} onChange={(e) => setRatePct(e.target.value)} placeholder="10" />
            </label>
            <label>
              Duration (months)
              <input inputMode="numeric" value={durationMonths} disabled={forever}
                onChange={(e) => setDurationMonths(e.target.value)} placeholder="24" />
            </label>
            <label className="row" style={{ gap: 8, alignItems: "center", marginTop: 22 }}>
              <input type="checkbox" checked={forever} onChange={(e) => setForever(e.target.checked)} style={{ width: "auto" }} />
              Pays forever
            </label>
            {!isNew ? (
              <label>
                Status
                <select value={status} onChange={(e) => setStatus(e.target.value as ReferrerStatus)}>
                  <option value="invited">Invited</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </label>
            ) : null}
          </div>
          <label>
            Internal notes
            <textarea value={notes ?? ""} onChange={(e) => setNotes(e.target.value)} placeholder="How you know them, agreed terms…" />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : isNew ? "Add referrer" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmModal({
  title, body, confirmLabel, onCancel, onConfirm
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h2>{title}</h2>
        <p>{body}</p>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-danger" disabled={busy}
            onClick={async () => { setBusy(true); await onConfirm(); }}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Leaderboard tab — no commission percentages, ever.
 * ------------------------------------------------------------------------- */

function LeaderboardTab() {
  const toast = useToast();
  const [rows, setRows] = useState<ReferrerLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchReferralLeaderboard()
      .then((res) => { if (active) setRows(res.leaderboard ?? []); })
      .catch((err) => toast.push("error", err instanceof Error ? err.message : "Could not load the leaderboard."))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [toast]);

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 70 }}>Rank</th>
            <th>Referrer</th>
            <th>Active companies</th>
            <th>Total sales (this month)</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={4}><div className="empty-state"><p className="muted">Loading leaderboard…</p></div></td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={4}><div className="empty-state"><h3>No earnings yet</h3><p>Once referred companies convert, they'll rank here.</p></div></td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.referrerId}>
                <td>
                  <span className="row" style={{ gap: 6 }}>
                    {r.rank <= 3 ? <Trophy size={14} style={{ color: r.rank === 1 ? "var(--warn)" : "var(--text-mute)" }} /> : null}
                    <strong>#{r.rank}</strong>
                  </span>
                </td>
                <td className="row-link-name">{r.referrerName}</td>
                <td className="tnum">{r.activeCompanyCount}</td>
                <td className="tnum"><strong>{formatRand(r.totalSalesCents)}</strong></td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
